import { useEffect } from "react";
import type { Feature } from "geojson";
import type mapboxgl from "mapbox-gl";
import { MapRef } from "react-map-gl";

interface UseMapHoverProps
{
    map: MapRef | undefined;
    layerId: string;
    setHoveredAlerts: ( alerts: Array<Feature> ) => void;
    setPopupCoords: ( coords: { lat: number, lng: number } | null ) => void;
    handleOpenModal: ( alerts: Array<Feature> ) => void;
    isSelectionState: boolean;
}

export function useMapHover( {
    map,
    layerId,
    setHoveredAlerts,
    setPopupCoords,
    handleOpenModal,
    isSelectionState,
}: UseMapHoverProps )
{

    useEffect( () =>
    {
        if ( map === undefined || map === null ) return;

        const handleMouseMove = ( e: mapboxgl.MapMouseEvent & mapboxgl.EventData ) =>
        {
            const features = map.queryRenderedFeatures( e.point, { layers: [ layerId ] } ) as Array<Feature>;

            if ( features.length > 0 )
            {
                const coords = { lat: e.lngLat.lat, lng: e.lngLat.lng };
                setHoveredAlerts( features );
                setPopupCoords( coords );
            }
            else
            {
                setHoveredAlerts( [] );
                setPopupCoords( null );
            }
        };

        const handleMouseEnter = ( e: mapboxgl.MapMouseEvent & mapboxgl.EventData ) =>
        {
            const features = e.features ?? [];
            if ( features.length > 0 )
            {
                setHoveredAlerts( features );
                setPopupCoords( { lat: e.lngLat.lat, lng: e.lngLat.lng } );
            }
        };

        const handleMouseLeave = () =>
        {
            setHoveredAlerts( [] );
            setPopupCoords( null );
        };

        const handleClick = ( e: mapboxgl.MapMouseEvent & mapboxgl.EventData ) =>
        {
            const features = map.queryRenderedFeatures( e.point, { layers: [ layerId ] } ) as Array<Feature>;
            if ( features.length > 0 && !isSelectionState )
            {
                setHoveredAlerts( features );
                setPopupCoords( { lat: e.lngLat.lat, lng: e.lngLat.lng } );
                handleOpenModal( features );
            }
        };

        map.on( "mousemove", handleMouseMove );
        map.on( "mouseenter", layerId, handleMouseEnter );
        map.on( "mouseleave", layerId, handleMouseLeave );
        map.on( "click", handleClick );

        return () =>
        {
            map.off( "mousemove", handleMouseMove );
            map.off( "mouseenter", layerId, handleMouseEnter );
            map.off( "mouseleave", layerId, handleMouseLeave );
            map.off( "click", handleClick );
        };
    }, [ map, layerId, setHoveredAlerts, setPopupCoords ] );

}
